import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const API = "/api";
const PAGE_SIZE = 20;
const INPUT_STYLE = { height: 30, padding: '0 10px', borderRadius: 6, background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: 12, width: '100%', boxSizing: 'border-box' };

function durationColor(weeks) {
  if (weeks >= 12) return '#F87171';
  if (weeks >= 6) return '#FBBF24';
  return '#4ADE80';
}

export default function BreakVerification() {
  const { authFetch, user, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [filterPlayer, setFilterPlayer] = useState('');
  const [form, setForm] = useState({ player_name: '', tour: 'ATP', week_start: '', tournament_skipped: '', estimated_weeks: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [openAttr, setOpenAttr] = useState(null); // composite key string
  const [attrForm, setAttrForm] = useState({});
  const [attrSaving, setAttrSaving] = useState(false);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (filterPlayer) params.set('player', filterPlayer);
    params.set('limit', PAGE_SIZE);
    params.set('offset', offset);
    authFetch(`${API}/admin/breaks/unlinked?${params}`)
      .then(r => r.json())
      .then(d => { setItems(d.items || []); setTotal(d.total || 0); })
      .catch(() => {});
  }, [authFetch, filterPlayer, offset]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!form.player_name || !form.week_start) return;
    setSaving(true);
    try {
      const res = await authFetch(`${API}/admin/breaks/manual`, {
        method: 'POST',
        body: JSON.stringify({
          player_name: form.player_name, tour: form.tour, week_start: form.week_start,
          tournament_skipped: form.tournament_skipped || null,
          estimated_weeks: form.estimated_weeks ? parseInt(form.estimated_weeks) : null,
          notes: form.notes || null,
        }),
      });
      const data = await res.json();
      if (data.status === 'created') {
        setMsg(`Break aggiunto per ${form.player_name}`);
        setForm({ player_name: '', tour: 'ATP', week_start: '', tournament_skipped: '', estimated_weeks: '', notes: '' });
        load();
        setTimeout(() => setMsg(null), 4000);
      }
    } catch (e) { setMsg('Errore durante il salvataggio'); }
    setSaving(false);
  };

  const itemKey = (item) => `${item.player_name}|${item.tour}|${item.season}|${item.week_start}`;

  const handleAttribute = async (item) => {
    const key = itemKey(item);
    const f = attrForm[key] || {};
    if (!f.causa) return;
    setAttrSaving(true);
    try {
      await authFetch(`${API}/admin/breaks/attribute`, {
        method: 'PUT',
        body: JSON.stringify({
          player_name: item.player_name, tour: item.tour, season: item.season, week_start: item.week_start,
          causa: f.causa, body_part: f.body_part || null,
          source_url: f.source_url || null, source_title: f.source_title || null,
          notes: f.notes || null,
        }),
      });
      setOpenAttr(null);
      setAttrForm(prev => { const n = { ...prev }; delete n[key]; return n; });
      setMsg(`Attribuzione salvata per ${item.player_name}`);
      load();
      setTimeout(() => setMsg(null), 4000);
    } catch (e) { setMsg('Errore attribuzione'); }
    setAttrSaving(false);
  };

  const updateAttr = (key, field, value) => {
    setAttrForm(prev => ({ ...prev, [key]: { ...(prev[key] || {}), [field]: value } }));
  };

  return (
    <div className="admin-page">
      <header className="header">
        <div className="header-left">
          <span className="logo-text" style={{ cursor: 'pointer' }} onClick={() => navigate('/chat')}>ERMENE</span>
          <span className="logo-subtitle">Break Monitor</span>
        </div>
        <div className="header-right">
          <span style={{ fontSize: 12, color: 'var(--risk-elevated)', fontWeight: 600 }}>
            {total.toLocaleString()} break senza fonte
          </span>
          <span className="header-subtitle">{user?.username}</span>
          <button className="theme-toggle" onClick={logout}>Esci</button>
        </div>
      </header>

      <div className="admin-content">
        {msg && <div style={{ background: 'rgba(74,222,128,0.15)', color: '#4ade80', padding: '8px 16px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{msg}</div>}

        <div className="breaks-filters">
          <input placeholder="Cerca player..." value={filterPlayer}
            onChange={e => { setFilterPlayer(e.target.value); setOffset(0); }}
            style={{ flex: 1 }} />
        </div>

        <div className="breaks-list">
          {items.map((item, i) => {
            const key = itemKey(item);
            const color = durationColor(item.break_total_weeks);
            const isOpen = openAttr === key;
            const af = attrForm[key] || {};
            return (
              <div key={i} style={{
                background: 'var(--surface)', borderRadius: 8, padding: '12px 16px', marginBottom: 8,
                borderLeft: `4px solid ${color}`, border: `1px solid var(--border)`, borderLeftWidth: 4, borderLeftColor: color,
                transition: 'border-color 0.2s',
              }}>
                {/* Row 1 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--text-primary)', flex: 1 }}>{item.player_name}</span>
                  <span className="role-badge role-operator" style={{ fontSize: 10 }}>{item.tour}</span>
                  {item.retirement === 1 && <span style={{ background: 'rgba(248,113,113,0.15)', color: '#F87171', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>Ret</span>}
                  <span style={{ fontSize: 22, fontWeight: 700, color, marginLeft: 8 }}>{item.break_total_weeks}<span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-secondary)' }}>w</span></span>
                </div>

                {/* Row 2 */}
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                  {item.week_start}
                  {item.prev_tourney && <span>{'  \u2192  '}{item.prev_tourney} ({item.prev_surface})</span>}
                  {item.prev_rank && <span>{'  |  Rank: '}{item.prev_rank}</span>}
                </div>

                {/* Attribute button */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                  <button onClick={() => setOpenAttr(isOpen ? null : key)}
                    style={{ padding: '2px 10px', fontSize: 11, background: 'transparent', border: '1px solid var(--accent)',
                      color: 'var(--accent)', borderRadius: 4, cursor: 'pointer' }}>
                    {isOpen ? 'Chiudi' : 'Attribuisci \u2192'}
                  </button>
                </div>

                {/* Attribution panel */}
                {isOpen && (
                  <div style={{ background: 'var(--surface-raised)', borderRadius: 6, padding: 12, marginTop: 8, border: '1px solid var(--accent)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                      <select value={af.causa || ''} onChange={e => updateAttr(key, 'causa', e.target.value)} style={{ ...INPUT_STYLE }}>
                        <option value="">Causa...</option>
                        <option value="infortunio">Infortunio</option>
                        <option value="riposo">Riposo</option>
                        <option value="personale">Personale</option>
                        <option value="sconosciuto">Sconosciuto</option>
                      </select>
                      <input placeholder="es. ginocchio, schiena..." value={af.body_part || ''} onChange={e => updateAttr(key, 'body_part', e.target.value)} style={INPUT_STYLE} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                      <input placeholder="https://..." value={af.source_url || ''} onChange={e => updateAttr(key, 'source_url', e.target.value)} style={INPUT_STYLE} />
                      <input placeholder="Titolo fonte" value={af.source_title || ''} onChange={e => updateAttr(key, 'source_title', e.target.value)} style={INPUT_STYLE} />
                    </div>
                    <textarea placeholder="Note (opzionale)" value={af.notes || ''} onChange={e => updateAttr(key, 'notes', e.target.value)} rows={2}
                      style={{ ...INPUT_STYLE, height: 'auto', padding: '6px 10px', resize: 'none', fontFamily: 'inherit', marginBottom: 8 }} />
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button onClick={() => setOpenAttr(null)}
                        style={{ padding: '4px 14px', fontSize: 11, background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer' }}>
                        Annulla</button>
                      <button onClick={() => handleAttribute(item)} disabled={!af.causa || attrSaving}
                        style={{ padding: '4px 14px', fontSize: 11, background: af.causa ? 'var(--accent-bright)' : 'transparent',
                          color: af.causa ? 'var(--bg)' : '#6b7280', border: af.causa ? 'none' : '1px solid var(--border)',
                          borderRadius: 6, cursor: af.causa ? 'pointer' : 'not-allowed', fontWeight: 600 }}>
                        {attrSaving ? 'Salvataggio...' : 'Salva'}</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {items.length === 0 && <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: 40 }}>Nessun break trovato</p>}
        </div>

        {total > PAGE_SIZE && (
          <div className="breaks-pagination">
            <button disabled={offset === 0} onClick={() => setOffset(o => Math.max(0, o - PAGE_SIZE))}>Precedente</button>
            <span>{offset + 1}-{Math.min(offset + PAGE_SIZE, total)} di {total.toLocaleString()}</span>
            <button disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(o => o + PAGE_SIZE)}>Successivo</button>
          </div>
        )}

        <div style={{ marginTop: 24, padding: 16, background: 'var(--surface-raised)', borderRadius: 8, border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Aggiungi Break Manuale</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 140px', gap: 8, marginBottom: 8 }}>
            <input placeholder="Player name" value={form.player_name} onChange={e => setForm(f => ({ ...f, player_name: e.target.value }))} style={INPUT_STYLE} />
            <select value={form.tour} onChange={e => setForm(f => ({ ...f, tour: e.target.value }))} style={INPUT_STYLE}>
              <option value="ATP">ATP</option><option value="WTA">WTA</option>
            </select>
            <input type="date" value={form.week_start} onChange={e => setForm(f => ({ ...f, week_start: e.target.value }))} style={INPUT_STYLE} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px', gap: 8, marginBottom: 8 }}>
            <input placeholder="Torneo saltato (opzionale)" value={form.tournament_skipped} onChange={e => setForm(f => ({ ...f, tournament_skipped: e.target.value }))} style={INPUT_STYLE} />
            <input type="number" placeholder="Settimane" value={form.estimated_weeks} onChange={e => setForm(f => ({ ...f, estimated_weeks: e.target.value }))} style={INPUT_STYLE} />
          </div>
          <textarea placeholder="Note (opzionale)" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
            style={{ ...INPUT_STYLE, height: 'auto', padding: '6px 10px', resize: 'none', fontFamily: 'inherit', marginBottom: 8 }} />
          <button onClick={handleAdd} disabled={saving || !form.player_name || !form.week_start}
            style={{ padding: '6px 20px', fontSize: 13, background: 'var(--accent-bright)', color: 'var(--bg)', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, opacity: saving || !form.player_name || !form.week_start ? 0.4 : 1 }}>
            {saving ? 'Salvataggio...' : 'Aggiungi'}
          </button>
        </div>
      </div>
    </div>
  );
}
